/* eslint-disable camelcase */
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { billingPipelineUseMocks } from 'src/constants/config';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type { InvisiblyApiResponse } from 'src/services/api/api-response.types';
import {
  getMockPipelineLineItems,
  getMockReviewFlagById,
  getMockReviewFlags,
  mockAiResults,
  mockBillingImports,
  mockBatches,
  mockMarkReviewFlagResolved,
} from './mocks/pipeline.fixtures';
import type {
  AIValidationResultDTO,
  BillingImportDTO,
  ListAiResultsParams,
  ListBatchesResponse,
  ListImportsParams,
  ListImportsResponse,
  ListPipelineLineItemsResponse,
  ListReviewFlagsParams,
  ListReviewFlagsResponse,
  ResolveReviewFlagBody,
  ReviewFlagDTO,
  TriggerIngestionBody,
  TriggerIngestionResponse,
} from './types/pipeline.types';

function normalizeListResponse<T>(response: unknown): { results: T[]; count: number } {
  if (response && typeof response === 'object') {
    const r = response as Record<string, unknown>;
    if (Array.isArray(r.results) && typeof r.count === 'number') {
      return { results: r.results as T[], count: r.count as number };
    }
    const item = r.item;
    if (item && typeof item === 'object') {
      const inner = item as Record<string, unknown>;
      if (Array.isArray(inner.results) && typeof inner.count === 'number') {
        return { results: inner.results as T[], count: inner.count as number };
      }
    }
  }
  return { results: [], count: 0 };
}

function normalizeItemResponse<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'item' in response) {
    return (response as InvisiblyApiResponse<T>).item;
  }
  return response as T;
}

const pipelineBasePath = 'b2b/billing/pipeline';

export const pipelineEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    listBillingImports: builder.query<ListImportsResponse, ListImportsParams | void>({
      queryFn: async(arg, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          let rows = [...mockBillingImports];
          const p = arg ?? {};
          if (p.source) {
            rows = rows.filter((r) => r.source === p.source);
          }
          if (p.status) {
            rows = rows.filter((r) => r.status === p.status);
          }
          if (p.start_date && rows.some((r) => r.raw_data?.start_date)) {
            rows = rows.filter((r) => (r.raw_data?.start_date ?? '') >= p.start_date!);
          }
          if (p.end_date && rows.some((r) => r.raw_data?.end_date)) {
            rows = rows.filter((r) => (r.raw_data?.end_date ?? '') <= p.end_date!);
          }
          const page = p.page ?? 1;
          const pageSize = p.page_size ?? 50;
          const start = (page - 1) * pageSize;
          return { data: { results: rows.slice(start, start + pageSize), count: rows.length } };
        }
        const params: Record<string, string | number> = {};
        if (arg) {
          if (arg.source) {
            params.source = arg.source;
          }
          if (arg.status) {
            params.status = arg.status;
          }
          if (arg.start_date) {
            params.start_date = arg.start_date;
          }
          if (arg.end_date) {
            params.end_date = arg.end_date;
          }
          if (arg.page != null) {
            params.page = arg.page;
          }
          if (arg.page_size != null) {
            params.page_size = arg.page_size;
          }
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/imports`,
          params,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeListResponse<BillingImportDTO>(res.data) };
      },
      providesTags: [{ type: APITagType.BillingPipelineImport, id: 'LIST' }],
    }),

    getBillingImport: builder.query<BillingImportDTO, number>({
      queryFn: async(id, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          const row = mockBillingImports.find((r) => r.id === id);
          if (!row) {
            return { error: { status: 404, data: 'Not found' } as never };
          }
          return { data: row };
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/imports/${id}`,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeItemResponse<BillingImportDTO>(res.data) };
      },
      providesTags: (_r, _e, id) => [{ type: APITagType.BillingPipelineImport, id: String(id) }],
    }),

    listBillingIngestionBatches: builder.query<ListBatchesResponse, void>({
      queryFn: async(_arg, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          return { data: { results: mockBatches, count: mockBatches.length } };
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/batches`,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeListResponse(res.data) };
      },
      providesTags: [{ type: APITagType.BillingPipelineImport, id: 'BATCHES' }],
    }),

    listBillingImportLineItems: builder.query<ListPipelineLineItemsResponse, { importId: number; page?: number; page_size?: number }>({
      queryFn: async({ importId, page, page_size }, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          const all = getMockPipelineLineItems(importId);
          return { data: { results: all, count: all.length } };
        }
        const params: Record<string, number> = {};
        if (page != null) {
          params.page = page;
        }
        if (page_size != null) {
          params.page_size = page_size;
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/imports/${importId}/line-items`,
          params,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeListResponse(res.data) };
      },
      providesTags: (_r, _e, { importId }) => [
        { type: APITagType.BillingPipelineImport, id: `${importId}-lines` },
      ],
    }),

    listReviewFlags: builder.query<ListReviewFlagsResponse, ListReviewFlagsParams | void>({
      queryFn: async(arg, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          const resolved = arg?.resolved ?? false;
          let rows = getMockReviewFlags(resolved);
          if (arg?.severity) {
            rows = rows.filter((r) => r.severity === arg.severity);
          }
          return { data: { results: rows, count: rows.length } };
        }
        const params: Record<string, string | number | boolean> = {};
        if (arg?.resolved !== undefined) {
          params.resolved = arg.resolved;
        }
        if (arg?.severity) {
          params.severity = arg.severity;
        }
        if (arg?.page != null) {
          params.page = arg.page;
        }
        if (arg?.page_size != null) {
          params.page_size = arg.page_size;
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/review-flags`,
          params,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeListResponse<ReviewFlagDTO>(res.data) };
      },
      providesTags: [{ type: APITagType.BillingPipelineReviewFlag, id: 'LIST' }],
    }),

    resolveReviewFlag: builder.mutation<ReviewFlagDTO, { id: number; body: ResolveReviewFlagBody }>({
      queryFn: async({ id, body }, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          mockMarkReviewFlagResolved(id);
          const updated = getMockReviewFlagById(id);
          if (!updated) {
            return { error: { status: 404, data: 'Not found' } as never };
          }
          return { data: { ...updated, resolved: true, resolved_at: new Date().toISOString() } };
        }
        const res = await baseQuery({
          method: 'patch',
          url: `${pipelineBasePath}/review-flags/${id}`,
          data: body,
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeItemResponse<ReviewFlagDTO>(res.data) };
      },
      invalidatesTags: [{ type: APITagType.BillingPipelineReviewFlag, id: 'LIST' }],
    }),

    listAiValidationResults: builder.query<{ results: AIValidationResultDTO[] }, ListAiResultsParams>({
      queryFn: async(arg, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          const filtered = mockAiResults.filter(
            (r) => r.content_type.id === arg.content_type_id && r.object_id === arg.object_id
          );
          return { data: { results: filtered } };
        }
        const res = await baseQuery({
          method: 'get',
          url: `${pipelineBasePath}/ai-results`,
          params: { content_type_id: arg.content_type_id, object_id: arg.object_id },
        });
        if (res.error) {
          return { error: res.error };
        }
        const raw = res.data as unknown;
        if (raw && typeof raw === 'object' && Array.isArray((raw as { results?: unknown }).results)) {
          return { data: { results: (raw as { results: AIValidationResultDTO[] }).results } };
        }
        const item = (raw as InvisiblyApiResponse<{ results: AIValidationResultDTO[] }>)?.item;
        if (item && Array.isArray(item.results)) {
          return { data: item };
        }
        return { data: { results: [] } };
      },
    }),

    triggerBillingImport: builder.mutation<TriggerIngestionResponse, TriggerIngestionBody>({
      queryFn: async(body, _api, _opts, baseQuery) => {
        if (billingPipelineUseMocks) {
          return {
            data: { task_enqueued: true, billing_import_id: 99 },
          };
        }
        const res = await baseQuery({
          method: 'post',
          url: `${pipelineBasePath}/imports/trigger`,
          data: body,
          successCodes: [200, 202],
        });
        if (res.error) {
          return { error: res.error };
        }
        return { data: normalizeItemResponse<TriggerIngestionResponse>(res.data) };
      },
      invalidatesTags: [{ type: APITagType.BillingPipelineImport, id: 'LIST' }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useListBillingImportsQuery,
  useGetBillingImportQuery,
  useListBillingIngestionBatchesQuery,
  useListBillingImportLineItemsQuery,
  useListReviewFlagsQuery,
  useResolveReviewFlagMutation,
  useListAiValidationResultsQuery,
  useTriggerBillingImportMutation,
} = pipelineEndpoints;
